/**
 * ─── Arena Betting Service ──────────────────────────────────
 * Handles bet placement, odds computation, payout calculation,
 * and match settlement.
 *
 * Double-entry: Bets → ledger entries (member debit, pool credit).
 * Payouts → ledger entries (pool debit, member credit, house commission).
 */

import type { Match, Bet, ArenaMember } from './arena.schema'

export class ArenaBettingService {
  /**
   * Compute potential payout for a bet.
   */
  static computePayout(amount: number, odds: number): number {
    return Math.round(amount * odds * 100) / 100
  }

  /**
   * Compute house commission on a winning bet payout.
   */
  static computeHouseCommission(payout: number, commissionRate: number): number {
    return Math.round(payout * commissionRate * 100) / 100
  }

  /**
   * Compute net payout to member after house commission.
   */
  static computeNetPayout(payout: number, commissionRate: number): number {
    const commission = ArenaBettingService.computeHouseCommission(payout, commissionRate)
    return Math.round((payout - commission) * 100) / 100
  }

  /**
   * Validate a bet before placement.
   */
  static validateBet(bet: Partial<Bet>, match: Match): { valid: boolean; error?: string } {
    if (match.status !== 'scheduled') {
      return { valid: false, error: 'Bets only accepted on scheduled matches' }
    }
    if (bet.amount! <= 0) {
      return { valid: false, error: 'Bet amount must be greater than 0' }
    }
    // Validate fighter selection
    if (bet.fighterId !== match.fighter1Id && bet.fighterId !== match.fighter2Id && bet.betType !== 'draw') {
      return { valid: false, error: 'Invalid fighter selection for this match' }
    }
    return { valid: true }
  }

  /**
   * Settle all bets for a match after result is declared.
   * Returns which bets won and lost based on match result.
   */
  static settleMatch(match: Match, bets: Bet[]): {
    winningBets: Bet[]
    losingBets: Bet[]
    totalPaidOut: number
    totalHouseCommission: number
  } {
    const winningBets: Bet[] = []
    const losingBets: Bet[] = []

    for (const bet of bets) {
      if (bet.status !== 'placed') continue

      let won = false
      if (match.result === 'fighter1_win' && bet.betType === 'fighter1') won = true
      else if (match.result === 'fighter2_win' && bet.betType === 'fighter2') won = true
      else if (match.result === 'draw' && bet.betType === 'draw') won = true

      if (won) {
        winningBets.push({
          ...bet,
          status: 'won',
          actualPayout: ArenaBettingService.computeNetPayout(
            bet.potentialPayout, match.houseCommission
          ),
          settledAt: Date.now(),
        })
      } else {
        losingBets.push({ ...bet, status: 'lost', settledAt: Date.now() })
      }
    }

    const totalPaidOut = winningBets.reduce((s, b) => s + (b.actualPayout ?? 0), 0)
    const totalHouseCommission = winningBets.reduce(
      (s, b) => s + ArenaBettingService.computeHouseCommission(b.potentialPayout, match.houseCommission), 0
    )

    return { winningBets, losingBets, totalPaidOut, totalHouseCommission }
  }

  /**
   * Get member betting stats.
   */
  static getMemberStats(member: ArenaMember, bets: Bet[]): {
    totalBets: number
    totalWagered: number
    totalWon: number
    winRate: number
  } {
    const memberBets = bets.filter(b => b.memberId === member.id)
    const won = memberBets.filter(b => b.status === 'won' || b.status === 'paid')
    return {
      totalBets: memberBets.length,
      totalWagered: memberBets.reduce((s, b) => s + b.amount, 0),
      totalWon: won.reduce((s, b) => s + (b.actualPayout ?? 0), 0),
      winRate: memberBets.length > 0 ? Math.round(won.length / memberBets.length * 100) : 0,
    }
  }

  /**
   * Compute opening odds based on fighter records.
   * Fighter with better record gets lower odds (favorite).
   */
  static computeOpeningOdds(fighter1: { wins: number; losses: number }, fighter2: { wins: number; losses: number }): {
    fighter1Odds: number
    fighter2Odds: number
    drawOdds: number
  } {
    const f1Total = fighter1.wins + fighter1.losses || 1
    const f2Total = fighter2.wins + fighter2.losses || 1
    const f1WinRate = fighter1.wins / f1Total
    const f2WinRate = fighter2.wins / f2Total

    // Favorite gets lower odds, underdog gets higher
    const fighter1Odds = Math.round((2 / Math.max(f1WinRate, 0.1)) * 100) / 100
    const fighter2Odds = Math.round((2 / Math.max(f2WinRate, 0.1)) * 100) / 100

    return {
      fighter1Odds: Math.max(1.1, fighter1Odds),
      fighter2Odds: Math.max(1.1, fighter2Odds),
      drawOdds: 5.0, // Fixed draw odds
    }
  }
}
